import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, Upload, X, Search, Users, UserPlus, Trophy, Store, Newspaper, Calendar, History, Shield, Medal } from "lucide-react";
import { StoresAdmin, NewsAdmin, TorneosAdmin, SeasonsAdmin, TeamsAdmin } from "../components/admin/ContentAdmin";
import { ResultsAdmin, InscritosAdmin } from "../components/admin/ResultsAdmin";
import { RankingEditor } from "../components/admin/RankingEditor";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPage = ({ user, authChecked }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const usersPerPage = 20;
  const navigate = useNavigate();

  // Rankings state
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [rankingPlayers, setRankingPlayers] = useState([]);
  const [rankingSearch, setRankingSearch] = useState('');
  const [photoModal, setPhotoModal] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (!authChecked) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadPendingRegistrations();
    loadAllUsers();
    loadRankingCollections();
  }, [user, navigate, authChecked]);

  const loadPendingRegistrations = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-registrations`, { withCredentials: true });
      setPendingUsers(res.data.pending || []);
    } catch (e) {
      console.error('Error loading pending registrations:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async (search = '', page = 0) => {
    try {
      const skip = page * usersPerPage;
      let url = `${API}/admin/users?skip=${skip}&limit=${usersPerPage}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const res = await axios.get(url, { withCredentials: true });
      setAllUsers(res.data.users || []);
      setTotalUsers(res.data.total || 0);
    } catch (e) {
      console.error('Error loading users:', e);
    }
  };

  const loadRankingCollections = async () => {
    try {
      const res = await axios.get(`${API}/ranking/collections`);
      setCollections(res.data.collections || []);
    } catch (e) {
      console.error('Error loading ranking collections:', e);
    }
  };

  const loadRankingPlayers = async (collectionName) => {
    try {
      let url = `${API}/ranking/collection/${encodeURIComponent(collectionName)}?limit=100`;
      if (rankingSearch) url += `&search=${encodeURIComponent(rankingSearch)}`;
      
      const res = await axios.get(url);
      setRankingPlayers(res.data.entries || []);
    } catch (e) {
      console.error('Error loading ranking players:', e);
    }
  };

  useEffect(() => {
    if (selectedCollection) {
      loadRankingPlayers(selectedCollection);
    }
  }, [selectedCollection, rankingSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    loadAllUsers(searchTerm, 0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadAllUsers(searchTerm, newPage);
  };

  const approveUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/approve-registration/${userId}`, {}, { withCredentials: true });
      setPendingUsers(pendingUsers.filter(u => u.user_id !== userId));
      loadAllUsers(searchTerm, currentPage);
    } catch (e) {
      console.error('Error approving user:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const rejectUser = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/reject-registration/${userId}`, {}, { withCredentials: true });
      setPendingUsers(pendingUsers.filter(u => u.user_id !== userId));
    } catch (e) {
      console.error('Error rejecting user:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const changeUserRole = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      setAllUsers(allUsers.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
    } catch (e) {
      console.error('Error changing role:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteUser = async (userId, userName) => {
    setActionLoading(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      setAllUsers(allUsers.filter(u => u.user_id !== userId));
      setTotalUsers(prev => prev - 1);
    } catch (e) {
      console.error('Error deleting user:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoModal || !photoUrl) return;
    
    setUploadingPhoto(true);
    try {
      await axios.put(
        `${API}/ranking/collection/${encodeURIComponent(selectedCollection)}/photo`,
        {
          photo_url: photoUrl,
          player_name: photoModal.Nombre,
          player_position: photoModal.Posicion
        },
        { withCredentials: true }
      );
      
      // Update local state
      setRankingPlayers(rankingPlayers.map(p => 
        p.Nombre === photoModal.Nombre ? { ...p, Photo: photoUrl } : p
      ));
      
      setPhotoModal(null);
      setPhotoUrl('');
      alert('Foto actualizada correctamente');
    } catch (e) {
      console.error('Error uploading photo:', e);
      alert('Error al actualizar la foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      moderator: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      user: 'bg-gray-500/20 text-gray-400 border-gray-500/50'
    };
    return badges[role] || badges.user;
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/50 to-black pt-24 pb-12 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/50 to-black pt-24 pb-12">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl md:text-4xl font-black text-center mb-2">
          <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
            Panel de Administración
          </span>
        </h1>
        <p className="text-center text-gray-400 mb-8">Gestiona usuarios, rankings, tiendas, noticias, torneos, temporadas y equipos</p>

        {/* Tabs */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex flex-wrap gap-1 bg-purple-900/30 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 py-2.5 px-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm ${
                activeTab === 'pending'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-purple-800/50'
              }`}
            >
              <UserPlus size={18} />
              <span className="hidden sm:inline">Solicitudes</span>
              {pendingUsers.length > 0 && (
                <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{pendingUsers.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 py-2.5 px-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm ${
                activeTab === 'users'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-purple-800/50'
              }`}
            >
              <Users size={18} />
              <span className="hidden sm:inline">Usuarios</span>
              <span className="text-xs">({totalUsers})</span>
            </button>
            <button
              onClick={() => setActiveTab('rankings')}
              className={`flex-1 py-2.5 px-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm ${
                activeTab === 'rankings'
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-purple-800/50'
              }`}
            >
              <Trophy size={18} />
              <span className="hidden sm:inline">Rankings</span>
            </button>
            {[['stores', 'Tiendas', Store], ['news', 'Noticias', Newspaper], ['torneos', 'Torneos', Calendar], ['results', 'Resultados', Medal], ['seasons', 'Temporadas', History], ['teams', 'Equipos', Shield]].map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex-1 py-2.5 px-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-purple-800/50'
                }`}
                data-testid={`admin-tab-${id}`}
                title={label}
              >
                <Icon size={18} />
                <span className="hidden lg:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto">
          {activeTab === 'pending' ? (
            /* Pending Registrations */
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                  <UserPlus size={24} />
                  Solicitudes de Registro
                </h2>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-sm font-bold">
                  {pendingUsers.length} pendientes
                </span>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400">Cargando solicitudes...</p>
                </div>
              ) : pendingUsers.length === 0 ? (
                <div className="text-center py-12 bg-purple-900/20 rounded-xl">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <p className="text-gray-400 text-lg">No hay solicitudes pendientes</p>
                  <p className="text-gray-500 text-sm mt-1">Las nuevas solicitudes aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((pendingUser) => (
                    <div 
                      key={pendingUser.user_id}
                      className="bg-purple-900/30 border border-purple-700/50 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                          {pendingUser.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <h3 className="font-bold text-white">{pendingUser.name}</h3>
                          <p className="text-gray-400 text-sm">{pendingUser.email}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            Solicitado: {formatDate(pendingUser.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-16 md:ml-0">
                        <button
                          onClick={() => approveUser(pendingUser.user_id)}
                          disabled={actionLoading === pendingUser.user_id}
                          className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg font-semibold text-white text-sm hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                          {actionLoading === pendingUser.user_id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                          Aprobar
                        </button>
                        <button
                          onClick={() => rejectUser(pendingUser.user_id)}
                          disabled={actionLoading === pendingUser.user_id}
                          className="px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-lg font-semibold text-red-400 text-sm hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                          Rechazar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'stores' ? (
            <div className="glass-card rounded-xl p-6"><StoresAdmin /></div>
          ) : activeTab === 'news' ? (
            <div className="glass-card rounded-xl p-6"><NewsAdmin /></div>
          ) : activeTab === 'torneos' ? (
            <div className="glass-card rounded-xl p-6"><TorneosAdmin /><InscritosAdmin /></div>
          ) : activeTab === 'results' ? (
            <div className="glass-card rounded-xl p-6"><ResultsAdmin /></div>
          ) : activeTab === 'seasons' ? (
            <div className="glass-card rounded-xl p-6"><SeasonsAdmin /></div>
          ) : activeTab === 'teams' ? (
            <div className="glass-card rounded-xl p-6"><TeamsAdmin /></div>
          ) : activeTab === 'users' ? (
            /* All Users */
            <div className="glass-card rounded-xl p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                  <Users size={24} />
                  Todos los Usuarios
                </h2>
                
                {/* Search */}
                <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre o email..."
                    className="flex-1 md:w-64 px-4 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pink-500"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-500 rounded-lg text-white font-semibold hover:bg-pink-600 transition-colors"
                  >
                    <Search size={20} />
                  </button>
                </form>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-purple-700/50">
                      <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm">Usuario</th>
                      <th className="text-left py-3 px-2 text-gray-400 font-semibold text-sm hidden md:table-cell">Email</th>
                      <th className="text-center py-3 px-2 text-gray-400 font-semibold text-sm">Rol</th>
                      <th className="text-center py-3 px-2 text-gray-400 font-semibold text-sm hidden md:table-cell">NirCoins</th>
                      <th className="text-right py-3 px-2 text-gray-400 font-semibold text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-purple-900/30 hover:bg-purple-900/20">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                u.name?.charAt(0)?.toUpperCase() || '?'
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-white truncate">{u.name}</p>
                              <p className="text-gray-500 text-xs md:hidden truncate">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-gray-400 text-sm hidden md:table-cell">{u.email}</td>
                        <td className="py-3 px-2 text-center">
                          <select
                            value={u.role || 'user'}
                            onChange={(e) => changeUserRole(u.user_id, e.target.value)}
                            disabled={actionLoading === u.user_id || u.user_id === user.user_id}
                            className={`px-2 py-1 rounded-lg border text-xs font-semibold bg-transparent cursor-pointer disabled:cursor-not-allowed ${getRoleBadge(u.role)}`}
                          >
                            <option value="user" className="bg-gray-900">Usuario</option>
                            <option value="moderator" className="bg-gray-900">Moderador</option>
                            <option value="admin" className="bg-gray-900">Admin</option>
                          </select>
                        </td>
                        <td className="py-3 px-2 text-center text-yellow-400 font-semibold hidden md:table-cell">
                          {u.nircoin_balance?.toFixed(0) || 0}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <button
                            onClick={() => deleteUser(u.user_id, u.name)}
                            disabled={actionLoading === u.user_id || u.user_id === user.user_id}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={u.user_id === user.user_id ? "No puedes eliminarte a ti mismo" : "Eliminar usuario"}
                          >
                            {actionLoading === u.user_id ? (
                              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                              </svg>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-purple-700/50">
                  <p className="text-gray-400 text-sm">
                    Mostrando {currentPage * usersPerPage + 1} - {Math.min((currentPage + 1) * usersPerPage, totalUsers)} de {totalUsers}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                      className="px-3 py-1 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-800/50 transition-colors"
                    >
                      Anterior
                    </button>
                    <span className="px-3 py-1 text-gray-400">
                      {currentPage + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1}
                      className="px-3 py-1 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-800/50 transition-colors"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}

              {allUsers.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-400">No se encontraron usuarios</p>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-card rounded-xl p-6"><RankingEditor /></div>
          )}
        </div>
      </div>

      {/* Photo Upload Modal */}
      {photoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-700 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                Foto de {photoModal.Nombre}
              </h3>
              <button 
                onClick={() => { setPhotoModal(null); setPhotoUrl(''); }}
                className="p-1 text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview */}
            <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-purple-900/50 border-2 border-purple-700">
              {photoUrl ? (
                <img 
                  src={photoUrl} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <Camera size={32} />
                </div>
              )}
            </div>

            {/* URL Input */}
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-1 block">URL de la imagen</label>
              <input
                type="url"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="w-full px-4 py-2 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              />
              <p className="text-xs text-gray-500 mt-1">Ingresa la URL de una imagen pública</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => { setPhotoModal(null); setPhotoUrl(''); }}
                className="flex-1 px-4 py-2 bg-gray-700 rounded-lg text-gray-300 font-semibold hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handlePhotoUpload}
                disabled={!photoUrl || uploadingPhoto}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-lg text-black font-bold hover:shadow-lg hover:shadow-yellow-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploadingPhoto ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Upload size={18} />
                )}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
